import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { ArrowLeft, Save, Loader2, Upload, X, FileText, Search, User, CheckCircle2, Circle, PenLine } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Prodotti per macro area
const PRODOTTI_PER_AREA = {
  ORTOPEDIA: [
    { value: "scarpa_ortopedica", label: "Scarpa ortopedica" },
    { value: "busto", label: "Busto" },
    { value: "carrozzina_elettrica", label: "Carrozzina elettrica" },
    { value: "apparecchio_acustico", label: "Apparecchio acustico" },
    { value: "carrozzina_leggera", label: "Carrozzina leggera" },
    { value: "seggiolone", label: "Seggiolone" },
    { value: "comunicatore", label: "Comunicatore" },
    { value: "plantare", label: "Plantare" },
    { value: "tutore", label: "Tutore" },
    { value: "protesi", label: "Protesi" },
    { value: "deambulatore", label: "Deambulatore" },
    { value: "altro_ortopedia", label: "Altro (Ortopedia)" }
  ],
  FINANZIARIO: [
    { value: "prestito_personale", label: "Prestito personale" },
    { value: "cessione_quinto", label: "Cessione del quinto" },
    { value: "mutuo", label: "Mutuo" },
    { value: "finanziamento", label: "Finanziamento" },
    { value: "consolidamento_debiti", label: "Consolidamento debiti" },
    { value: "altro_finanziario", label: "Altro (Finanziario)" }
  ],
  CAF: [
    { value: "dichiarazione_730", label: "Dichiarazione 730" },
    { value: "isee", label: "ISEE" },
    { value: "unico", label: "Modello Unico" },
    { value: "red", label: "Modello RED" },
    { value: "imu_tasi", label: "IMU/TASI" },
    { value: "successione", label: "Successione" },
    { value: "contratto_locazione", label: "Contratto locazione" },
    { value: "altro_caf", label: "Altro (CAF)" }
  ],
  PATRONATO: [
    { value: "pensione_vecchiaia", label: "Pensione di vecchiaia" },
    { value: "pensione_anticipata", label: "Pensione anticipata" },
    { value: "pensione_invalidita", label: "Pensione invalidità" },
    { value: "assegno_sociale", label: "Assegno sociale" },
    { value: "naspi", label: "NASpI" },
    { value: "maternita", label: "Maternità" },
    { value: "infortunio", label: "Infortunio" },
    { value: "malattia", label: "Malattia" },
    { value: "reversibilita", label: "Reversibilità" },
    { value: "altro_patronato", label: "Altro (Patronato)" }
  ],
  LEGALE: [
    { value: "consulenza_legale", label: "Consulenza legale" },
    { value: "causa_civile", label: "Causa civile" },
    { value: "causa_lavoro", label: "Causa di lavoro" },
    { value: "recupero_crediti", label: "Recupero crediti" },
    { value: "separazione_divorzio", label: "Separazione/Divorzio" },
    { value: "successione_legale", label: "Successione" },
    { value: "contratti", label: "Contratti" },
    { value: "altro_legale", label: "Altro (Legale)" }
  ],
  ASSICURAZIONE: [
    { value: "rc_auto", label: "RC Auto" },
    { value: "polizza_vita", label: "Polizza vita" },
    { value: "polizza_casa", label: "Polizza casa" },
    { value: "polizza_salute", label: "Polizza salute" },
    { value: "polizza_infortuni", label: "Polizza infortuni" },
    { value: "sinistro", label: "Gestione sinistro" },
    { value: "altro_assicurazione", label: "Altro (Assicurazione)" }
  ],
  LUCE_E_GAS: [
    { value: "voltura_luce", label: "Voltura luce" },
    { value: "voltura_gas", label: "Voltura gas" },
    { value: "subentro_luce", label: "Subentro luce" },
    { value: "subentro_gas", label: "Subentro gas" },
    { value: "nuovo_allaccio_luce", label: "Nuovo allaccio luce" },
    { value: "nuovo_allaccio_gas", label: "Nuovo allaccio gas" },
    { value: "cambio_fornitore_luce", label: "Cambio fornitore luce" },
    { value: "cambio_fornitore_gas", label: "Cambio fornitore gas" },
    { value: "reclamo_bolletta", label: "Reclamo bolletta" },
    { value: "altro_luce_gas", label: "Altro (Luce e Gas)" }
  ]
};

const MACRO_AREE_INFO = {
  ORTOPEDIA: { label: "Ortopedia", color: "from-blue-500 to-blue-600", icon: "🦴" },
  FINANZIARIO: { label: "Finanziario", color: "from-green-500 to-green-600", icon: "💰" },
  CAF: { label: "CAF", color: "from-purple-500 to-purple-600", icon: "📋" },
  PATRONATO: { label: "Patronato", color: "from-orange-500 to-orange-600", icon: "🏛️" },
  LEGALE: { label: "Legale", color: "from-red-500 to-red-600", icon: "⚖️" },
  ASSICURAZIONE: { label: "Assicurazione", color: "from-teal-500 to-teal-600", icon: "🛡️" },
  LUCE_E_GAS: { label: "Luce e Gas", color: "from-yellow-500 to-orange-500", icon: "⚡" }
};

const NewOrder = ({ user, isEdit = false }) => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const macroAreaFromUrl = searchParams.get('area');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  
  // Client search state
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [searchingClients, setSearchingClients] = useState(false);
  const clientSearchRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    codice_fiscale: "",
    casa_appartenenza: "",
    prodotto_richiesto: "",
    macro_area: macroAreaFromUrl || "",
    note: "",
    client_id: "",
    client_name: ""
  });
  
  // Animation state for order creation
  const [showCreatingAnimation, setShowCreatingAnimation] = useState(false);
  const [creationStep, setCreationStep] = useState(0);
  
  const CREATION_STEPS = [
    { label: "Verifica dati cliente", delay: 800 },
    { label: "Creazione pratica", delay: 1200 },
    { label: "Caricamento documenti", delay: 1000 },
    { label: "Registrazione nel sistema", delay: 1500 },
    { label: "Finalizzazione ordine", delay: 1000 }
  ];
  
  // Get products based on selected macro area
  const availableProducts = formData.macro_area 
    ? PRODOTTI_PER_AREA[formData.macro_area] || []
    : [];
  
  const areaInfo = formData.macro_area 
    ? MACRO_AREE_INFO[formData.macro_area] 
    : null;
  
  useEffect(() => {
    if (isEdit && orderId) {
      fetchOrder();
    }
  }, [isEdit, orderId]);
  
  // Reset product when macro area changes
  useEffect(() => {
    if (!isEdit) {
      setFormData(prev => ({ ...prev, prodotto_richiesto: "" }));
    }
  }, [formData.macro_area]);
  
  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      const order = response.data;
      setFormData({
        nome: order.nome || "",
        cognome: order.cognome || "",
        telefono: order.telefono || "",
        codice_fiscale: order.codice_fiscale || "",
        casa_appartenenza: order.casa_appartenenza || "",
        prodotto_richiesto: order.prodotto_richiesto || "",
        macro_area: order.macro_area || "",
        note: order.note || "",
        client_id: order.client_id || "",
        client_name: order.client_name || ""
      });
      // If order has a client, set the search field
      if (order.client_name) {
        setClientSearch(order.client_name);
        setSelectedClient({ client_id: order.client_id, nome: order.nome, cognome: order.cognome });
      }
    } catch (error) {
      toast.error("Errore nel caricamento ordine");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Client search functions
  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    
    setSearchingClients(true);
    try {
      const response = await axios.get(`${API}/clients/search`, {
        params: { q: query, limit: 8 },
        withCredentials: true
      });
      setClientResults(response.data);
      setShowClientDropdown(true);
    } catch (error) {
      console.error("Errore ricerca clienti:", error);
    } finally {
      setSearchingClients(false);
    }
  };
  
  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    
    // Debounce search
    clearTimeout(window.clientSearchTimeout);
    window.clientSearchTimeout = setTimeout(() => {
      searchClients(value);
    }, 300);
  };
  
  const selectClient = (client) => {
    setSelectedClient(client);
    const fullName = `${client.nome || ""} ${client.cognome || ""}`.trim();
    setClientSearch(fullName);
    setShowClientDropdown(false);
    
    // Auto-fill form fields
    setFormData(prev => ({
      ...prev,
      nome: client.nome || prev.nome,
      cognome: client.cognome || prev.cognome,
      codice_fiscale: client.codice_fiscale || prev.codice_fiscale,
      telefono: client.telefono || prev.telefono,
      client_id: client.client_id,
      client_name: fullName
    }));
    
    toast.success(`Cliente selezionato: ${fullName}`);
  };
  
  const clearSelectedClient = () => {
    setSelectedClient(null);
    setClientSearch("");
    setFormData(prev => ({
      ...prev,
      client_id: "",
      client_name: ""
    }));
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const handleProductChange = (value) => {
    const product = availableProducts.find(p => p.value === value);
    setFormData(prev => ({ ...prev, prodotto_richiesto: product?.label || value }));
  };
  
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const totalFiles = files.length + newFiles.length;
    
    if (totalFiles > 5) {
      toast.error("Massimo 5 documenti consentiti");
      return;
    }
    
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const oversizedFiles = newFiles.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error("File troppo grande. Massimo 5MB per documento");
      return;
    }
    
    const pdfFiles = newFiles.filter(f => f.type === "application/pdf");
    if (pdfFiles.length !== newFiles.length) {
      toast.warning("Solo file PDF consentiti");
    }
    
    setFiles(prev => [...prev, ...pdfFiles].slice(0, 5));
  };
  
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome || !formData.telefono || 
        !formData.codice_fiscale || !formData.casa_appartenenza || !formData.prodotto_richiesto) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (!isEdit && !formData.macro_area) {
      toast.error("Seleziona una macro area");
      return;
    }
    
    setSaving(true);
    
    // Show animation for new orders only
    if (!isEdit) {
      setShowCreatingAnimation(true);
      setCreationStep(0);
      
      // Animate through steps
      for (let i = 0; i < CREATION_STEPS.length; i++) {
        await new Promise(resolve => setTimeout(resolve, CREATION_STEPS[i].delay));
        setCreationStep(i + 1);
      }
    }
    
    try {
      if (isEdit) {
        const editData = { ...formData };
        await axios.put(`${API}/orders/${orderId}`, editData, { withCredentials: true });
        toast.success("Ordine aggiornato");
        navigate(`/orders/${orderId}`);
      } else {
        const submitData = new FormData();
        submitData.append("nome", formData.nome);
        submitData.append("cognome", formData.cognome);
        submitData.append("telefono", formData.telefono);
        submitData.append("codice_fiscale", formData.codice_fiscale);
        submitData.append("casa_appartenenza", formData.casa_appartenenza);
        submitData.append("prodotto_richiesto", formData.prodotto_richiesto);
        submitData.append("macro_area", formData.macro_area);
        if (formData.note) {
          submitData.append("note", formData.note);
        }
        if (formData.client_id) {
          submitData.append("client_id", formData.client_id);
          submitData.append("client_name", formData.client_name);
        }
        
        files.forEach(file => {
          submitData.append("files", file);
        });
        
        const response = await axios.post(`${API}/orders`, submitData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        
        // Show success state briefly
        await new Promise(resolve => setTimeout(resolve, 800));
        toast.success("Ordine creato con successo! 🎉");
        navigate(`/area/${formData.macro_area}`);
      }
    } catch (error) {
      console.error("Errore:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Errore sconosciuto";
      toast.error(isEdit ? `Errore aggiornamento: ${errorMessage}` : `Errore creazione: ${errorMessage}`);
      setShowCreatingAnimation(false);
    } finally {
      setSaving(false);
    }
  };

  const getCurrentProductValue = () => {
    const product = availableProducts.find(p => p.label === formData.prodotto_richiesto);
    return product?.value || "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="new-order-page">
      {/* Order Creation Animation Overlay */}
      {showCreatingAnimation && (
        <div className="fixed inset-0 z-[100] bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95 backdrop-blur-md flex items-center justify-center">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in zoom-in-95 duration-300">
            {/* Writing Person Animation */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                {/* Person writing animation */}
                <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                  <div className="relative">
                    {/* Body */}
                    <div className="w-16 h-20 bg-gradient-to-b from-blue-500 to-blue-600 rounded-t-full relative">
                      {/* Head */}
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-10 h-10 bg-amber-200 rounded-full">
                        {/* Face */}
                        <div className="absolute top-3 left-2 w-1.5 h-1.5 bg-slate-700 rounded-full"></div>
                        <div className="absolute top-3 right-2 w-1.5 h-1.5 bg-slate-700 rounded-full"></div>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-3 h-1 bg-pink-300 rounded-full"></div>
                      </div>
                      {/* Writing arm with pen */}
                      <div className="absolute top-2 -right-4 origin-left animate-writing">
                        <div className="w-8 h-2 bg-amber-200 rounded-full transform rotate-45">
                          <PenLine className="w-4 h-4 text-slate-700 absolute -right-2 -top-1 transform rotate-45" />
                        </div>
                      </div>
                      {/* Left arm */}
                      <div className="absolute top-2 -left-2 w-6 h-2 bg-amber-200 rounded-full transform -rotate-30"></div>
                    </div>
                    {/* Desk */}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-3 bg-amber-700 rounded-sm"></div>
                    {/* Paper on desk */}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-6 bg-white rounded-sm shadow-sm border border-slate-200">
                      <div className="p-1 space-y-0.5">
                        <div className="w-full h-0.5 bg-slate-200 rounded animate-pulse"></div>
                        <div className="w-3/4 h-0.5 bg-slate-200 rounded animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-full h-0.5 bg-slate-200 rounded animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating papers */}
                <div className="absolute -top-2 -right-2 animate-float">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
                <div className="absolute -bottom-2 -left-2 animate-float" style={{animationDelay: '0.5s'}}>
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-bold text-center text-slate-900 mb-2">
              Creazione ordine in corso...
            </h2>
            <p className="text-sm text-slate-500 text-center mb-6">
              Attendi mentre prepariamo la tua pratica
            </p>
            
            {/* Progress Steps */}
            <div className="space-y-3">
              {CREATION_STEPS.map((step, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-500 ${
                    creationStep > index 
                      ? 'bg-green-50 border border-green-200' 
                      : creationStep === index 
                        ? 'bg-blue-50 border border-blue-200 animate-pulse' 
                        : 'bg-slate-50 border border-slate-100'
                  }`}
                >
                  <div className={`flex-shrink-0 transition-all duration-300 ${
                    creationStep > index ? 'scale-110' : ''
                  }`}>
                    {creationStep > index ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : creationStep === index ? (
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-300" />
                    )}
                  </div>
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    creationStep > index 
                      ? 'text-green-700' 
                      : creationStep === index 
                        ? 'text-blue-700' 
                        : 'text-slate-400'
                  }`}>
                    {step.label}
                  </span>
                  {creationStep > index && (
                    <span className="ml-auto text-xs text-green-500 font-medium">Completato</span>
                  )}
                </div>
              ))}
            </div>
            
            {/* Progress bar */}
            <div className="mt-6">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${(creationStep / CREATION_STEPS.length) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-400 text-center mt-2">
                {Math.round((creationStep / CREATION_STEPS.length) * 100)}% completato
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(isEdit ? `/orders/${orderId}` : formData.macro_area ? `/area/${formData.macro_area}` : "/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              {areaInfo && (
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${areaInfo.color} flex items-center justify-center text-lg`}>
                  {areaInfo.icon}
                </div>
              )}
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isEdit ? "Modifica Ordine" : `Nuovo Ordine${areaInfo ? ` - ${areaInfo.label}` : ''}`}
              </h1>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Macro Area Selection (only for new orders without pre-selected area) */}
          {!isEdit && !macroAreaFromUrl && (
            <div className="card-hover p-6">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">Seleziona Area *</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(MACRO_AREE_INFO).map(([key, info]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, macro_area: key }))}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.macro_area === key
                        ? 'border-slate-900 bg-slate-50 shadow-md'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`macro-area-${key.toLowerCase()}`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center text-xl mb-2`}>
                      {info.icon}
                    </div>
                    <span className="text-sm font-medium text-slate-900">{info.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Show selected area info */}
          {areaInfo && macroAreaFromUrl && (
            <div className={`p-4 rounded-xl bg-gradient-to-r ${areaInfo.color} text-white`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{areaInfo.icon}</span>
                <div>
                  <div className="font-semibold">{areaInfo.label}</div>
                  <div className="text-sm opacity-80">Area selezionata</div>
                </div>
              </div>
            </div>
          )}
          
          <div className="card-hover p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Dati Cliente</h2>
            
            {/* Client Search */}
            <div className="mb-6">
              <Label htmlFor="client-search" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Cerca Cliente Esistente
              </Label>
              <div className="relative mt-1.5" ref={clientSearchRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="client-search"
                    value={clientSearch}
                    onChange={handleClientSearchChange}
                    placeholder="Cerca per nome, cognome o codice fiscale..."
                    className="pl-10 pr-10"
                    data-testid="client-search-input"
                  />
                  {selectedClient && (
                    <button
                      type="button"
                      onClick={clearSelectedClient}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {searchingClients && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
                  )}
                </div>
                
                {/* Client search results dropdown */}
                {showClientDropdown && clientResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-y-auto">
                    {clientResults.map((client) => (
                      <button
                        key={client.client_id}
                        type="button"
                        onClick={() => selectClient(client)}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                        data-testid={`client-option-${client.client_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                            {client.nome?.charAt(0)}{client.cognome?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              {client.nome} {client.cognome}
                            </div>
                            <div className="text-xs text-slate-500">
                              CF: {client.codice_fiscale}
                              {client.n_tessera && ` • Tessera: ${client.n_tessera}`}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {showClientDropdown && clientResults.length === 0 && clientSearch.length >= 2 && !searchingClients && (
                  <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg p-4 text-center text-slate-500">
                    Nessun cliente trovato
                  </div>
                )}
              </div>
              
              {selectedClient && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <User className="w-4 h-4" />
                  <span>Cliente associato: <strong>{formData.client_name}</strong></span>
                </div>
              )}
              
              <p className="mt-2 text-xs text-slate-500">
                Cerca un cliente esistente oppure compila manualmente i campi sottostanti
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Mario"
                  className="mt-1.5"
                  data-testid="input-nome"
                />
              </div>
              
              <div>
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  name="cognome"
                  value={formData.cognome}
                  onChange={handleChange}
                  placeholder="Rossi"
                  className="mt-1.5"
                  data-testid="input-cognome"
                />
              </div>
              
              <div>
                <Label htmlFor="telefono">Telefono *</Label>
                <Input
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="+39 333 1234567"
                  className="mt-1.5"
                  data-testid="input-telefono"
                />
              </div>
              
              <div>
                <Label htmlFor="codice_fiscale">Codice Fiscale *</Label>
                <Input
                  id="codice_fiscale"
                  name="codice_fiscale"
                  value={formData.codice_fiscale}
                  onChange={handleChange}
                  placeholder="RSSMRA85M01H501Z"
                  className="mt-1.5 uppercase"
                  data-testid="input-cf"
                />
              </div>
              
              <div className="sm:col-span-2">
                <Label htmlFor="casa_appartenenza">Indirizzo / Riferimento *</Label>
                <Input
                  id="casa_appartenenza"
                  name="casa_appartenenza"
                  value={formData.casa_appartenenza}
                  onChange={handleChange}
                  placeholder="Via Roma 1, 00100 Roma"
                  className="mt-1.5"
                  data-testid="input-casa-appartenenza"
                />
              </div>
            </div>
          </div>
          
          <div className="card-hover p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Pratica / Servizio</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="prodotto_richiesto">Tipo Pratica *</Label>
                {formData.macro_area ? (
                  <Select 
                    value={getCurrentProductValue()} 
                    onValueChange={handleProductChange}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="input-prodotto">
                      <SelectValue placeholder="Seleziona tipo pratica..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProducts.map((product) => (
                        <SelectItem key={product.value} value={product.value}>
                          {product.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1.5 text-sm text-slate-500 italic">
                    Seleziona prima una macro area per vedere i tipi di pratica disponibili
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="note">Note (opzionale)</Label>
                <Textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  placeholder="Note aggiuntive..."
                  className="mt-1.5 min-h-24"
                  data-testid="input-note"
                />
              </div>
            </div>
          </div>
          
          {!isEdit && (
            <div className="card-hover p-6">
              <h2 className="text-sm font-semibold text-slate-900 mb-2">Documenti (opzionale)</h2>
              <p className="text-xs text-slate-500 mb-4">Carica fino a 5 PDF</p>
              
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-blue-300 transition-colors">
                  <input
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="pdf-upload"
                    data-testid="pdf-upload-input"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Clicca per caricare PDF</p>
                    <p className="text-xs text-slate-400 mt-1">{files.length}/5 documenti</p>
                  </label>
                </div>
                
                {files.length > 0 && (
                  <div className="space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={`file-${index}-${file.name}`}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        data-testid={`uploaded-file-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-red-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                              {file.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`remove-file-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(formData.macro_area ? `/area/${formData.macro_area}` : "/dashboard")}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.macro_area}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="submit-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEdit ? "Aggiornamento..." : "Creazione..."}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEdit ? "Aggiorna Ordine" : "Crea Ordine"}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default NewOrder;
